// src/components/admin/DocumentsTab.tsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Avatar,
  Link,
  SelectChangeEvent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ScannerIcon from '@mui/icons-material/Scanner';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import StorageIcon from '@mui/icons-material/Storage';
import { useTranslation } from 'react-i18next';
import {
  getDocuments,
  getDocumentStatistics,
  deleteDocument,
  formatFileSize,
  getFileTypeLabel,
} from '../../services/documentService';
import { AdminDocument, DocumentStatistics } from '../../types/document';

const DocumentsTab: React.FC = () => {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState<AdminDocument[]>([]);
  const [statistics, setStatistics] = useState<DocumentStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  // Filters
  const [search, setSearch] = useState('');
  const [fileTypeFilter, setFileTypeFilter] = useState<string>('all');
  const [scannedFilter, setScannedFilter] = useState<string>('all');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<AdminDocument | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // View dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<AdminDocument | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: Record<string, any> = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      };

      if (search) filters.search = search;
      if (fileTypeFilter !== 'all') filters.file_type = fileTypeFilter;
      if (scannedFilter !== 'all') filters.is_scanned = scannedFilter === 'scanned';

      const [docsRes, statsRes] = await Promise.all([
        getDocuments(filters),
        statistics ? Promise.resolve(statistics) : getDocumentStatistics(),
      ]);

      setDocuments(docsRes.documents || []);
      setTotal(docsRes.total || 0);
      if (!statistics) setStatistics(statsRes);
    } catch (err: any) {
      console.error('Failed to fetch documents:', err);
      setError(err.response?.data?.detail || t('documents.errorFetching'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, fileTypeFilter, scannedFilter, statistics, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(0);
  }, [search, fileTypeFilter, scannedFilter]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleFileTypeChange = (event: SelectChangeEvent<string>) => {
    setFileTypeFilter(event.target.value);
  };

  const handleScannedChange = (event: SelectChangeEvent<string>) => {
    setScannedFilter(event.target.value);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDocument = (doc: AdminDocument) => {
    setSelectedDocument(doc);
    setViewDialogOpen(true);
  };

  const handleDeleteClick = (doc: AdminDocument) => {
    setDocumentToDelete(doc);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    try {
      setDeleteLoading(true);
      await deleteDocument(documentToDelete.id);
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
      setStatistics(null); // Refresh stats
      fetchData();
    } catch (err: any) {
      console.error('Failed to delete document:', err);
      setError(err.response?.data?.detail || t('documents.errorDeleting'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('pdf')) return <PictureAsPdfIcon color="error" />;
    if (mimeType.includes('image')) return <ImageIcon color="primary" />;
    return <InsertDriveFileIcon color="action" />;
  };

  if (loading && documents.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {t('documents.title')}
      </Typography>

      {/* Statistics */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <DescriptionIcon sx={{ fontSize: 32, color: 'primary.main', mb: 0.5 }} />
                <Typography variant="h5" fontWeight="bold">{statistics.total_documents}</Typography>
                <Typography variant="caption" color="text.secondary">{t('documents.totalDocuments')}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <StorageIcon sx={{ fontSize: 32, color: 'info.main', mb: 0.5 }} />
                <Typography variant="h5" fontWeight="bold">{statistics.total_size_mb?.toFixed(1) || 0} MB</Typography>
                <Typography variant="caption" color="text.secondary">{t('documents.totalSize')}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <ScannerIcon sx={{ fontSize: 32, color: 'warning.main', mb: 0.5 }} />
                <Typography variant="h5" fontWeight="bold">{statistics.scanned_count}</Typography>
                <Typography variant="caption" color="text.secondary">{t('documents.scanned')}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <CloudUploadIcon sx={{ fontSize: 32, color: 'success.main', mb: 0.5 }} />
                <Typography variant="h5" fontWeight="bold">{statistics.recent_uploads}</Typography>
                <Typography variant="caption" color="text.secondary">{t('documents.recentUploads')}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
        <TextField
          placeholder={t('documents.searchPlaceholder')}
          value={search}
          onChange={handleSearchChange}
          size="small"
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('documents.fileType')}</InputLabel>
          <Select
            value={fileTypeFilter}
            label={t('documents.fileType')}
            onChange={handleFileTypeChange}
          >
            <MenuItem value="all">{t('documents.all')}</MenuItem>
            <MenuItem value="application/pdf">PDF</MenuItem>
            <MenuItem value="image/jpeg">JPEG</MenuItem>
            <MenuItem value="image/png">PNG</MenuItem>
            <MenuItem value="image/gif">GIF</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('documents.source')}</InputLabel>
          <Select
            value={scannedFilter}
            label={t('documents.source')}
            onChange={handleScannedChange}
          >
            <MenuItem value="all">{t('documents.all')}</MenuItem>
            <MenuItem value="scanned">{t('documents.scanned')}</MenuItem>
            <MenuItem value="uploaded">{t('documents.uploaded')}</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Documents Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 700 }}>{t('documents.document')}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{t('documents.owner')}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{t('documents.type')}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{t('documents.size')}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{t('documents.date')}</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">{t('documents.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.length > 0 ? (
              documents.map((doc) => (
                <TableRow key={doc.id} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      {getFileIcon(doc.file_type)}
                      <Box>
                        <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 200 }}>
                          {doc.title || doc.filename}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                          {doc.filename}
                        </Typography>
                      </Box>
                      {doc.is_scanned && (
                        <Chip
                          icon={<ScannerIcon sx={{ fontSize: 14 }} />}
                          label={t('documents.scanned')}
                          size="small"
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.main' }}>
                        {doc.user_name?.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>{doc.user_name}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 120 }}>{doc.user_email}</Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getFileTypeLabel(doc.file_type)}
                      size="small"
                      color={doc.file_type.includes('pdf') ? 'error' : doc.file_type.includes('image') ? 'primary' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatFileSize(doc.file_size)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(doc.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title={t('documents.view')}>
                        <IconButton size="small" onClick={() => handleViewDocument(doc)} color="info">
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('documents.download')}>
                        <IconButton
                          size="small"
                          component={Link}
                          href={doc.file_url}
                          target="_blank"
                          color="primary"
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('documents.delete')}>
                        <IconButton size="small" onClick={() => handleDeleteClick(doc)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <DescriptionIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {t('documents.noDocuments')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {documents.length > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={t('common.rowsPerPage')}
        />
      )}

      {/* View Document Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('documents.documentDetails')}</DialogTitle>
        <DialogContent dividers>
          {selectedDocument && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                {getFileIcon(selectedDocument.file_type)}
                <Box>
                  <Typography variant="h6">{selectedDocument.title || selectedDocument.filename}</Typography>
                  <Typography variant="body2" color="text.secondary">{selectedDocument.filename}</Typography>
                </Box>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t('documents.owner')}</Typography>
                  <Typography variant="body2">{selectedDocument.user_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{selectedDocument.user_email}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t('documents.size')}</Typography>
                  <Typography variant="body2">{formatFileSize(selectedDocument.file_size)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t('documents.type')}</Typography>
                  <Typography variant="body2">{getFileTypeLabel(selectedDocument.file_type)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t('documents.source')}</Typography>
                  <Typography variant="body2">
                    {selectedDocument.is_scanned ? t('documents.scanned') : t('documents.uploaded')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">{t('documents.createdAt')}</Typography>
                  <Typography variant="body2">{new Date(selectedDocument.created_at).toLocaleString()}</Typography>
                </Grid>
              </Grid>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>{t('common.close')}</Button>
          {selectedDocument && (
            <Button
              variant="contained"
              component={Link}
              href={selectedDocument.file_url}
              target="_blank"
              startIcon={<DownloadIcon />}
            >
              {t('documents.download')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('documents.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('documents.deleteConfirmMessage', { name: documentToDelete?.title || documentToDelete?.filename })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? <CircularProgress size={20} /> : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentsTab;
